/**
 * DecisionFlow — DrawPlanManager
 * data-fit LLC · Confidential
 * Domain: §45 Draw / Guarantee / Clawback (m073)
 * Spec authority: decisionflow_draw_guarantee_clawback_spec_v1_1.md §3 + §4
 * API Contract: api_contract_v1_31.md §45
 * Wizard substages: draw_plan_definition · draw_position_assignment · draw_balance_initialization
 * Feature gate: feature.tier.draw_plans — enterprise only. 403 DRAW_FEATURE_UNAVAILABLE → GateStatusBanner.
 * tenant_super_admin excluded (NN-36).
 * State derived from approved_by / submitted_at — no plan_status column on draw_plan table.
 * onReject={null} — reject endpoint not confirmed for §45.
 * Path: ui/src/components/tenant-admin/DrawPlanManager.jsx
 *
 * S312 — OI-S307-UI-THEME: Full CSS token compliance pass.
 *   StatusPill prop corrected: state= → status= (shared StatusPill expects `status` prop).
 *   inputClass updated: added focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))] token.
 *   All other token usage already compliant from prior pass.
 *   No logic, workflow, or structural changes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DRAW_TYPES = [
  { value: 'recoverable',     label: 'Recoverable Draw' },
  { value: 'non_recoverable', label: 'Non-Recoverable Guarantee' },
];

const RECOVERY_SCHEDULES = [
  { value: 'full_available_earnings', label: 'Full available earnings' },
  { value: 'pro_rata_monthly',        label: 'Pro-rata monthly' },
  { value: 'fixed_amount',            label: 'Fixed amount per period' },
];

const EMPTY_FORM = {
  plan_code:               '',
  plan_name:               '',
  draw_type:               'recoverable',
  advance_amount:          '',
  recovery_schedule:       'full_available_earnings',
  max_recovery_per_period: '',
  effective_start_date:    '',
  effective_end_date:      '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(plan) {
  if (plan.approved_by)                           return 'approved';
  if (plan.submitted_at || plan.submitted_by)     return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function getErrorMessage(error, fallback) {
  return error?.message || fallback;
}

// ---------------------------------------------------------------------------
// Shared class strings — matches PlanAssignmentManager M040 token pattern
// ---------------------------------------------------------------------------

const thClass   = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass   = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary    = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary  = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnDanger     = 'rounded-lg border border-[rgb(var(--color-danger))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass    = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass    = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function DrawPlanManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.draw')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  // Note: DrawPlanManager is plan-level, not component-scoped.
  // planComponentId prop accepted for API consistency but unused for filtering.

  // List state
  const [plans, setPlans]           = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError]   = useState('');

  // Detail / approval state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');

  // Approval action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState('');

  // Toast
  const [toast, setToast] = useState(null);

  // Feature gate
  const [featureUnavailable, setFeatureUnavailable] = useState(false);

  // ---------------------------------------------------------------------------
  // Embedded mode state — substage wizard (draw_position_assignment, draw_balance_initialization)
  // Only active when embedded=true. Standalone mode ignores these entirely.
  // ---------------------------------------------------------------------------

  // Which substage tab is active: 'definition' | 'assignment' | 'balance'
  const [substage, setSubstage] = useState('definition');

  // The draw plan selected for position assignment (substage 2+)
  const [selectedDrawPlanId, setSelectedDrawPlanId] = useState(null);

  // Substage 2 — draw_position_assignment
  const [authorizations, setAuthorizations]     = useState([]);
  const [authLoading, setAuthLoading]           = useState(false);
  const [authError, setAuthError]               = useState('');
  const [newPositionId, setNewPositionId]       = useState('');
  const [positions, setPositions]               = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [authSaving, setAuthSaving]             = useState(false);

  // Substage 3 — draw_balance_initialization
  const [balanceRows, setBalanceRows]   = useState({}); // { [draw_authorization_id]: opening_balance string }
  const [balanceSaving, setBalanceSaving] = useState(false);
  const [balanceError, setBalanceError]   = useState('');
  const [balanceSaved, setBalanceSaved]   = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const loadPlans = useCallback(async () => {
    if (!user) return;
    setListLoading(true);
    setListError('');
    try {
      // C7 — S689 plan_id scoping: filter draw plans to this plan when planId injected.
      const data = await apiGet(`/api/v1/draw-plans${planId != null ? '?plan_id=' + planId : ''}`);
      setPlans(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'DRAW_FEATURE_UNAVAILABLE') {
        setFeatureUnavailable(true);
        return;
      }
      setListError(getErrorMessage(error, 'Unable to load draw plans.'));
    } finally {
      setListLoading(false);
    }
  }, [user]);

  const loadPlanDetail = useCallback(async (planId) => {
    setDetailLoading(true);
    setActionError('');
    try {
      const data = await apiGet(`/api/v1/draw-plans/${planId}`);
      setSelectedPlan(data);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load plan detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  // ---------------------------------------------------------------------------
  // Embedded mode — data fetching for substages 2 and 3
  // ---------------------------------------------------------------------------

  const loadAuthorizations = useCallback(async (drawPlanId) => {
    if (!drawPlanId) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      const data = await apiGet(`/api/v1/draw-authorizations?draw_plan_id=${drawPlanId}`);
      const rows = Array.isArray(data) ? data : data?.data || [];
      setAuthorizations(rows);
      // Pre-populate balanceRows with existing opening_balance values
      const balanceInit = {};
      rows.forEach((a) => {
        balanceInit[a.draw_authorization_id] = a.opening_balance != null ? String(a.opening_balance) : '';
      });
      setBalanceRows(balanceInit);
    } catch (error) {
      setAuthError(getErrorMessage(error, 'Unable to load draw authorizations.'));
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const loadPositions = useCallback(async () => {
    if (positions.length > 0) return; // already loaded
    setPositionsLoading(true);
    try {
      const data = await apiGet('/api/v1/positions');
      setPositions(Array.isArray(data) ? data : data?.data || []);
    } catch (_) {}
    finally { setPositionsLoading(false); }
  }, [positions.length]);

  const authorizePosition = useCallback(async () => {
    if (!newPositionId || !selectedDrawPlanId) return;
    setAuthSaving(true);
    setAuthError('');
    try {
      await apiPost('/api/v1/draw-authorizations', {
        draw_plan_id: selectedDrawPlanId,
        position_id:  parseInt(newPositionId, 10),
      });
      setNewPositionId('');
      await loadAuthorizations(selectedDrawPlanId);
      showToast('Position authorized.');
    } catch (error) {
      setAuthError(getErrorMessage(error, 'Unable to authorize position.'));
    } finally {
      setAuthSaving(false);
    }
  }, [newPositionId, selectedDrawPlanId, loadAuthorizations, showToast]);

  const advanceToAssignment = useCallback(async (drawPlanId) => {
    setSelectedDrawPlanId(drawPlanId);
    await loadPositions();
    await loadAuthorizations(drawPlanId);
    // PATCH substage progression
    try {
      await apiPost('/api/v1/wizard/draw-substages/draw_position_assignment', { draw_plan_id: drawPlanId });
    } catch (_) {} // non-blocking — substage tracking only
    setSubstage('assignment');
  }, [loadPositions, loadAuthorizations]);

  const advanceToBalance = useCallback(async () => {
    try {
      await apiPost('/api/v1/wizard/draw-substages/draw_balance_initialization', { draw_plan_id: selectedDrawPlanId });
    } catch (_) {} // non-blocking
    setSubstage('balance');
  }, [selectedDrawPlanId]);

  const saveBalances = useCallback(async () => {
    setBalanceSaving(true);
    setBalanceError('');
    setBalanceSaved(false);
    try {
      await apiPost('/api/v1/wizard/draw-substages/draw_balance_initialization', {
        draw_plan_id: selectedDrawPlanId,
        balances: Object.entries(balanceRows).map(([id, val]) => ({
          draw_authorization_id: parseInt(id, 10),
          opening_balance:       parseFloat(val) || 0,
        })),
      });
      setBalanceSaved(true);
      showToast('Opening balances saved.');
    } catch (error) {
      setBalanceError(getErrorMessage(error, 'Unable to save balances.'));
    } finally {
      setBalanceSaving(false);
    }
  }, [selectedDrawPlanId, balanceRows, showToast]);

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------

  const updateForm = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setFormError('');
  }, []);

  const isRecoverable = form.draw_type === 'recoverable';

  const validateForm = useCallback(() => {
    if (!form.plan_code.trim())        return 'Plan code is required.';
    if (!form.plan_name.trim())        return 'Plan name is required.';
    if (!form.draw_type)               return 'Draw type is required.';
    if (!form.advance_amount || Number(form.advance_amount) <= 0)
      return 'Advance amount must be a positive number.';
    if (form.draw_type === 'recoverable' && !form.recovery_schedule)
      return 'Recovery schedule is required for recoverable plans.';
    if (!form.effective_start_date)    return 'Effective start date is required.';
    return '';
  }, [form]);

  const buildPayload = useCallback(() => {
    const payload = {
      plan_code:            form.plan_code.trim(),
      plan_name:            form.plan_name.trim(),
      draw_type:            form.draw_type,
      advance_amount:       form.advance_amount,
      recovery_schedule:    form.draw_type === 'recoverable' ? form.recovery_schedule : null,
      max_recovery_per_period: form.max_recovery_per_period
        ? form.max_recovery_per_period
        : null,
      effective_start_date: form.effective_start_date,
      effective_end_date:   form.effective_end_date || null,
    };
    return payload;
  }, [form]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const createPlan = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) { setFormError(validationError); return; }
    setSaving(true);
    setFormError('');
    try {
      const created = await apiPost('/api/v1/draw-plans', buildPayload());
      await loadPlans();
      setShowCreateForm(false);
      resetForm();
      showToast('Draw plan created.');
      // Open the new plan in detail/approval view
      if (created?.draw_plan_id) {
        await loadPlanDetail(created.draw_plan_id);
      }
    } catch (error) {
      setFormError(getErrorMessage(error, 'Unable to create draw plan.'));
    } finally {
      setSaving(false);
    }
  }, [buildPayload, loadPlanDetail, loadPlans, resetForm, showToast, validateForm]);

  const submitPlan = useCallback(async () => {
    if (!selectedPlan) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/draw-plans/${selectedPlan.draw_plan_id}/submit`, {});
      await loadPlanDetail(selectedPlan.draw_plan_id);
      await loadPlans();
      showToast('Draw plan submitted for approval.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to submit draw plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, selectedPlan, showToast]);

  const approvePlan = useCallback(async () => {
    if (!selectedPlan) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/draw-plans/${selectedPlan.draw_plan_id}/approve`, {});
      await loadPlanDetail(selectedPlan.draw_plan_id);
      await loadPlans();
      showToast('Draw plan approved.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to approve draw plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, selectedPlan, showToast]);

  // ---------------------------------------------------------------------------
  // Derived state for ApprovalPanel
  // ---------------------------------------------------------------------------

  const planState    = selectedPlan ? deriveState(selectedPlan) : 'draft';
  const currentUserId = user?.user_id || user?.id || null;

  // ---------------------------------------------------------------------------
  // Feature gate — render nothing but the banner
  // ---------------------------------------------------------------------------

  if (featureUnavailable) {
    return <GateStatusBanner feature="Draw Plans" />;
  }

  // ---------------------------------------------------------------------------
  // Auth guard
  // ---------------------------------------------------------------------------

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Draw Plans</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // EMBEDDED MODE — three-substage wizard
  // Shown when opened from PlanDetailView modifier card.
  // Substage 1: select an approved draw plan.
  // Substage 2: authorize positions (POST /draw-authorizations).
  // Substage 3: initialize opening balances.
  // ---------------------------------------------------------------------------

  if (embedded) {
    const approvedPlans = plans.filter((p) => deriveState(p) === 'approved');
    const positionMap   = Object.fromEntries(positions.map((p) => [String(p.position_id), p]));

    // Substage tab config
    const SUBSTAGES = [
      { key: 'definition',  label: '1 · Select Plan'         },
      { key: 'assignment',  label: '2 · Authorize Positions'  },
      { key: 'balance',     label: '3 · Opening Balances'     },
    ];

    return (
      <div className="flex flex-col gap-4">

        {/* Substage tab bar */}
        <div className="flex items-center gap-1 border-b border-[rgb(var(--color-border))] pb-0">
          {SUBSTAGES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                if (s.key === 'assignment' && !selectedDrawPlanId) return;
                if (s.key === 'balance'    && !selectedDrawPlanId) return;
                setSubstage(s.key);
              }}
              disabled={(s.key === 'assignment' || s.key === 'balance') && !selectedDrawPlanId}
              className={[
                'px-3 py-2 text-tok-xs font-medium border-b-2 -mb-px transition',
                substage === s.key
                  ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                  : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] disabled:opacity-40 disabled:cursor-not-allowed',
              ].join(' ')}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* ── Substage 1 — Plan Selection ── */}
        {substage === 'definition' && (
          <div className="flex flex-col gap-3">
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Select an approved draw plan to assign to positions. Plans must be approved before
              position authorization.
            </p>
            {listLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}
            {listError  && <p className="text-tok-sm text-[rgb(var(--color-danger))]">{listError}</p>}
            {!listLoading && approvedPlans.length === 0 && (
              <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-6 text-center">
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No approved draw plans</p>
                <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Create and approve a draw plan from Draw Plans in the left nav before assigning positions.
                </p>
              </div>
            )}
            {approvedPlans.map((plan) => (
              <div
                key={plan.draw_plan_id}
                className={[
                  'flex items-center justify-between gap-3 rounded-xl border p-4 transition',
                  selectedDrawPlanId === plan.draw_plan_id
                    ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5'
                    : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]',
                ].join(' ')}
              >
                <div>
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{plan.plan_name}</p>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                    {plan.plan_code} · {DRAW_TYPES.find((t) => t.value === plan.draw_type)?.label || plan.draw_type}
                    {plan.advance_amount != null && ` · ${Number(plan.advance_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                  </p>
                </div>
                <button
                  type="button"
                  className={btnPrimary}
                  onClick={() => advanceToAssignment(plan.draw_plan_id)}
                  disabled={authSaving}
                >
                  {selectedDrawPlanId === plan.draw_plan_id ? 'Selected ✓' : 'Select →'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Substage 2 — Position Authorization ── */}
        {substage === 'assignment' && (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Draw plan: <strong className="text-[rgb(var(--color-text))]">
                {plans.find((p) => p.draw_plan_id === selectedDrawPlanId)?.plan_name}
              </strong>
              <span className="ml-3 text-[10px] italic">
                Four-eyes enforced — authorizing user must differ from plan creator (NN-10).
              </span>
            </div>

            {authError && (
              <p className="text-tok-xs text-[rgb(var(--color-danger))]">{authError}</p>
            )}

            {/* Add position form */}
            {!readOnly && (
              <div className="flex items-end gap-2 flex-wrap">
                <label className={`${labelClass} flex-1 min-w-[180px]`}>
                  Add position
                  <select
                    className={inputClass}
                    value={newPositionId}
                    onChange={(e) => setNewPositionId(e.target.value)}
                    disabled={positionsLoading}
                  >
                    <option value="">
                      {positionsLoading ? 'Loading positions…' : '— Select position —'}
                    </option>
                    {positions
                      .filter((p) => !authorizations.some((a) => String(a.position_id) === String(p.position_id)))
                      .map((p) => (
                        <option key={p.position_id} value={p.position_id}>
                          {p.full_name} ({p.employee_id || p.position_id})
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  type="button"
                  className={btnPrimary}
                  onClick={authorizePosition}
                  disabled={!newPositionId || authSaving}
                >
                  {authSaving ? 'Authorizing…' : '+ Authorize'}
                </button>
              </div>
            )}

            {/* Authorization table */}
            {authLoading ? (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading authorizations…</p>
            ) : authorizations.length === 0 ? (
              <p className="text-tok-sm italic text-[rgb(var(--color-text-muted))]">
                No positions authorized yet.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Position</th>
                      <th className={thClass}>Employee ID</th>
                      <th className={thClass}>Authorized by</th>
                      <th className={thClass}>Authorized at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {authorizations.map((auth) => {
                      const pos = positionMap[String(auth.position_id)];
                      return (
                        <tr key={auth.draw_authorization_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{pos?.full_name || auth.position_id}</td>
                          <td className={tdClass}>{pos?.employee_id || '—'}</td>
                          <td className={tdClass}>{auth.authorized_by || '—'}</td>
                          <td className={tdClass}>{auth.authorized_at ? auth.authorized_at.slice(0, 10) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {authorizations.length > 0 && !readOnly && (
              <div className="flex justify-end">
                <button type="button" className={btnPrimary} onClick={advanceToBalance}>
                  Continue to Opening Balances →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Substage 3 — Balance Initialization ── */}
        {substage === 'balance' && (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Set opening draw balance for each authorized position. Leave blank to default to
              the plan advance amount at calculation time.
            </div>

            {balanceError && (
              <p className="text-tok-xs text-[rgb(var(--color-danger))]">{balanceError}</p>
            )}
            {balanceSaved && (
              <p className="text-tok-xs text-[rgb(var(--color-success))]">Opening balances saved.</p>
            )}

            {authorizations.length === 0 ? (
              <p className="text-tok-sm italic text-[rgb(var(--color-text-muted))]">
                No authorized positions — go back to substage 2 to authorize positions first.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Position</th>
                      <th className={thClass}>Employee ID</th>
                      <th className={thClass}>Opening Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {authorizations.map((auth) => {
                      const pos = positionMap[String(auth.position_id)];
                      return (
                        <tr key={auth.draw_authorization_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{pos?.full_name || auth.position_id}</td>
                          <td className={tdClass}>{pos?.employee_id || '—'}</td>
                          <td className={tdClass}>
                            {readOnly ? (
                              <span>{balanceRows[auth.draw_authorization_id] || '—'}</span>
                            ) : (
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className={inputClass}
                                value={balanceRows[auth.draw_authorization_id] ?? ''}
                                onChange={(e) => setBalanceRows((prev) => ({
                                  ...prev,
                                  [auth.draw_authorization_id]: e.target.value,
                                }))}
                                placeholder="0.00 (optional)"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {authorizations.length > 0 && !readOnly && (
              <div className="flex justify-end">
                <button type="button" className={btnPrimary} onClick={saveBalances} disabled={balanceSaving}>
                  {balanceSaving ? 'Saving…' : 'Save Opening Balances'}
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // STANDALONE MODE — existing behaviour unchanged below this line
  // ---------------------------------------------------------------------------

  if (selectedPlan) {
    const state = deriveState(selectedPlan);
    return (
      <section className="flex flex-col gap-6">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              type="button"
              className={btnSecondary}
              onClick={() => { setSelectedPlan(null); setActionError(''); }}
            >
              ← Back to list
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              {selectedPlan.plan_name}
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {selectedPlan.plan_code} ·{' '}
              {DRAW_TYPES.find((t) => t.value === selectedPlan.draw_type)?.label || selectedPlan.draw_type}
            </p>
          </div>
          <StatusPill status={state} />
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {actionError}
          </div>
        )}

        {/* Plan detail card */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Plan details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Draw type</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {DRAW_TYPES.find((t) => t.value === selectedPlan.draw_type)?.label || selectedPlan.draw_type}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Advance amount</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedPlan.advance_amount != null
                  ? Number(selectedPlan.advance_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
                  : '—'}
              </dd>
            </div>
            {selectedPlan.draw_type === 'recoverable' && (
              <>
                <div>
                  <dt className="text-[rgb(var(--color-text-muted))] font-medium">Recovery schedule</dt>
                  <dd className="text-[rgb(var(--color-text))]">
                    {RECOVERY_SCHEDULES.find((s) => s.value === selectedPlan.recovery_schedule)?.label
                      || selectedPlan.recovery_schedule || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-[rgb(var(--color-text-muted))] font-medium">Max recovery / period</dt>
                  <dd className="text-[rgb(var(--color-text))]">
                    {selectedPlan.max_recovery_per_period
                      ? Number(selectedPlan.max_recovery_per_period).toLocaleString(undefined, { minimumFractionDigits: 2 })
                      : 'Full available earnings'}
                  </dd>
                </div>
              </>
            )}
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective start</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPlan.effective_start_date || '—'}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective end</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPlan.effective_end_date || 'Open-ended'}</dd>
            </div>
          </dl>
        </div>

        {/* Approval panel */}
        {detailLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
        ) : (
          <ApprovalPanel
            variant="draw_plan"
            currentState={state}
            submittedBy={selectedPlan.submitted_by}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? submitPlan : null}
            onApprove={state === 'pending_approval' ? approvePlan : null}
            onReject={null}
            approvedBy={selectedPlan.approved_by}
            approvedAt={selectedPlan.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // List view (default)
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {/* Toast */}
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8B
          </p>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Draw Plans</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Configure recoverable draw and non-recoverable guarantee plans. Plans must be approved before
            assignment to positions.
          </p>
        </div>
        <button
          type="button"
          className={btnPrimary}
          onClick={() => { setShowCreateForm(true); setFormError(''); }}
          disabled={showCreateForm}
        >
          + New draw plan
        </button>
      </header>

      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {listError}
        </div>
      )}

      {/* Create form */}
      {showCreateForm && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New draw plan</h3>

          {formError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelClass}>
              Plan code
              <input
                type="text"
                className={inputClass}
                value={form.plan_code}
                onChange={(e) => updateForm('plan_code', e.target.value)}
                placeholder="e.g. ENT_AE_DRAW_2026"
              />
            </label>
            <label className={labelClass}>
              Plan name
              <input
                type="text"
                className={inputClass}
                value={form.plan_name}
                onChange={(e) => updateForm('plan_name', e.target.value)}
                placeholder="e.g. Enterprise AE Recoverable Draw FY2026"
              />
            </label>
            <label className={labelClass}>
              Draw type
              <select
                className={inputClass}
                value={form.draw_type}
                onChange={(e) => updateForm('draw_type', e.target.value)}
              >
                {DRAW_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              {form.draw_type === 'recoverable' ? 'Advance amount' : 'Guarantee floor amount'}
              <input
                type="number"
                min="0.01"
                step="0.01"
                className={inputClass}
                value={form.advance_amount}
                onChange={(e) => updateForm('advance_amount', e.target.value)}
                placeholder="0.00"
              />
            </label>

            {/* Recovery schedule — shown only for recoverable */}
            {isRecoverable && (
              <>
                <label className={labelClass}>
                  Recovery schedule
                  <select
                    className={inputClass}
                    value={form.recovery_schedule}
                    onChange={(e) => updateForm('recovery_schedule', e.target.value)}
                  >
                    {RECOVERY_SCHEDULES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
                <label className={labelClass}>
                  Max recovery per period
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputClass}
                    value={form.max_recovery_per_period}
                    onChange={(e) => updateForm('max_recovery_per_period', e.target.value)}
                    placeholder="Leave blank for full available earnings"
                  />
                </label>
              </>
            )}

            <label className={labelClass}>
              Effective start date
              <input
                type="date"
                className={inputClass}
                value={form.effective_start_date}
                onChange={(e) => updateForm('effective_start_date', e.target.value)}
              />
            </label>
            <label className={labelClass}>
              Effective end date
              <input
                type="date"
                className={inputClass}
                value={form.effective_end_date}
                onChange={(e) => updateForm('effective_end_date', e.target.value)}
              />
              <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Leave blank for open-ended plan.
              </span>
            </label>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <button type="button" className={btnPrimary} onClick={createPlan} disabled={saving}>
              {saving ? 'Creating…' : 'Create draw plan'}
            </button>
            <button
              type="button"
              className={btnSecondary}
              onClick={() => { setShowCreateForm(false); resetForm(); }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Plans table */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Draw plans</h3>
          {listLoading && (
            <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</span>
          )}
          <button type="button" className={btnSecondary} onClick={loadPlans} disabled={listLoading}>
            Refresh
          </button>
        </div>

        {plans.length === 0 && !listLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
            No draw plans have been created. Use the button above to create the first plan.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-tok-sm border-collapse">
              <thead>
                <tr>
                  <th className={thClass}>Plan code</th>
                  <th className={thClass}>Plan name</th>
                  <th className={thClass}>Type</th>
                  <th className={thClass}>Advance amount</th>
                  <th className={thClass}>Effective start</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => {
                  const state = deriveState(plan);
                  return (
                    <tr
                      key={plan.draw_plan_id}
                      className="hover:bg-[rgb(var(--color-surface-muted))]"
                    >
                      <td className={tdClass}>{plan.plan_code}</td>
                      <td className={tdClass}>{plan.plan_name}</td>
                      <td className={tdClass}>
                        {DRAW_TYPES.find((t) => t.value === plan.draw_type)?.label || plan.draw_type}
                      </td>
                      <td className={tdClass}>
                        {plan.advance_amount != null
                          ? Number(plan.advance_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
                          : '—'}
                      </td>
                      <td className={tdClass}>{plan.effective_start_date || '—'}</td>
                      <td className={tdClass}>
                        <StatusPill status={state} />
                      </td>
                      <td className={tdClass}>
                        <button
                          type="button"
                          className={btnSecondary}
                          onClick={() => loadPlanDetail(plan.draw_plan_id)}
                        >
                          {state === 'draft' ? 'Review / Submit' : state === 'pending_approval' ? 'Review / Approve' : 'View'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
